import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import type { Moment } from '@this/src/lib/moment';
import moment from '@this/src/lib/moment';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';

import { sortBarApprovalsLeadtime } from '@this/components/organization/dashboard/share/graph';
import type { DashboardProps } from '../../share';
import type { BarApprovalsLeadtime } from '../../share/approvals_leadtime_pile_up_graph';
import { chartOptions, getBarData, getBarHeight } from '../../share/approvals_leadtime_pile_up_graph';
import type { DashboardRequest } from '../../share/fetch';
import { fetchDashboard } from '../../share/fetch';
import { RefreshCacheButton } from '../../share/refresh_cache_button';

const USECASE = { section: 'approval_lead_time_by_department', pattern: 'cache' };

interface State {
  loading: boolean;
  leadtimes: BarApprovalsLeadtime[];
  lastUpdatedAt: Moment | null;
  refreshAt: Moment | null;
}

interface Response {
  leadtimes: State['leadtimes'];
  last_updated_at: string;
}

type Request = DashboardRequest<State> & { proceed?: () => void };

const fetchSummary = async ({ setState, proceed, ...props }: Request) => {
  setState(state => ({ ...state, loading: true }));
  const result = await fetchDashboard<Response>({ ...USECASE, ...props });
  setState(state => ({
    ...state,
    loading: false,
    leadtimes: result.leadtimes,
    lastUpdatedAt: result.last_updated_at ? moment(result.last_updated_at) : null
  }));
  proceed?.();
};

const DashboardApprovalsLeadtimeByDepartmentSection: React.FC<DashboardProps> = props => {
  const [state, setState] = useState<State>({
    loading: true,
    leadtimes: [],
    lastUpdatedAt: null,
    refreshAt: null
  });
  const { loading, leadtimes, lastUpdatedAt, refreshAt } = state;
  const { urlQuery, organizationId } = props;
  const { type, from, to } = urlQuery;

  const data = useMemo(() => getBarData(sortBarApprovalsLeadtime(leadtimes, 'desc'), 'default'), [leadtimes]);
  const height = useMemo(() => getBarHeight(leadtimes), [leadtimes]);

  const handleRefreshCache = useCallback(() => {
    setState(state => ({ ...state, refreshAt: moment() }));
  }, [setState]);

  useEffect(() => {
    fetchSummary({ setState, ...props });
  }, [type, from, to, refreshAt, props.refreshAt]);

  return (
    <>
      <h3 className="graph_title">承認リードタイム（費用負担部署別）</h3>
      {loading ? (
        <SimpleLoading />
      ) : (
        <Bar data={data} width={550} height={height} options={{ indexAxis: 'y', ...chartOptions }} />
      )}
      <RefreshCacheButton
        cacheType={USECASE.section}
        dateType={type}
        from={from}
        to={to}
        organizationId={organizationId}
        lastUpdatedAt={lastUpdatedAt}
        onRefreshCache={handleRefreshCache}
      />
    </>
  );
};

export default DashboardApprovalsLeadtimeByDepartmentSection;
